import type { Patch } from "@freightsimple/shared/types";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";

import { dangerousGoodsDescribeHazardClass } from "@/Shared/DangerousGoods/describeHazardClass";
import { DangerousGoodsSelectUNNumberDialog } from "@/Shared/DangerousGoods/Dialogs/DangerousGoodsSelectUNNumberDialog";
import { useState } from "react";

interface DangerousGoodsInputProps {
  lineItem: Patch<LineItem>;
  onChange: (update: Patch<LineItem>) => void;
  errorMessage: ErrorMessageType;
  forceValidation: boolean;
  // True when the shipment touches Canada, so the Canadian TDG net explosive
  // quantity is collected for Class 1 explosives.
  canadianTdgRules: boolean;
}

function DescriptionForDangerousItem_(props: DangerousGoodsInputProps) {
  if (props.lineItem.dangerousUnNumber === undefined) {
    return <>Dangerous</>;
  }

  // A UN number only exists once this option is the chosen one, so this branch
  // always renders on the dropdown's selected row — which is painted brand
  // blue with white ink. A `T` colour role pins a fixed grey that survives
  // that repaint, so the hazard class read grey-on-blue. Both parts inherit
  // the row's ink instead; size and weight already carry the hierarchy.
  return (
    <Row gap={2} align="baseline">
      <T size="xl" weight="bold">
        {props.lineItem.dangerousUnNumber}
      </T>
      <T size="base">
        {dangerousGoodsDescribeHazardClass(
          props.lineItem.dangerousClassification || "",
        )}
      </T>
    </Row>
  );
}

const DangerousGoodsInput: React.FC<DangerousGoodsInputProps> = (
  props: DangerousGoodsInputProps,
) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Counts opens so the dialog remounts each time. It seeds its working copy of
  // the line item from props on mount, and it hands that whole copy back on
  // Done — so a dialog mounted once at page load would write a snapshot taken
  // before the user had typed anything, wiping the fields they filled in first.
  const [detailsOpenCount, setDetailsOpenCount] = useState(0);

  function onOptionSelected(dangerous: boolean) {
    if (dangerous) {
      // The dropdown stays on its previous value until the dialog reports
      // details back — cancelling shouldn't leave the item marked dangerous
      // with nothing filled in.
      setDetailsOpenCount((count) => count + 1);
      setDetailsOpen(true);
    } else {
      props.onChange({
        isDangerous: false,
        dangerousUnNumber: undefined,
        dangerousClassification: undefined,
        dangerousNumberPackages: undefined,
        dangerousPackagingType: undefined,
        dangerousProperShippingName: undefined,
        dangerousPackingGroup: undefined,
        dangerousTechnicalName: undefined,
        dangerousNetExplosiveQuantityKg: undefined,
      });
    }

    return;
  }

  const selectedValue =
    props.lineItem.isDangerous === undefined
      ? undefined
      : String(props.lineItem.isDangerous);

  return (
    <ShowFormErrors show={Boolean(props.forceValidation)}>
      <FormItem
        label={
          <>
            Dangerous?{" "}
            <Tooltip
              interactive
              variant="info"
              content={
                <Stack gap={2}>
                  <T weight="bold">Dangerous</T>
                  <T size="xs" color="secondary">
                    If your freight is dangerous/hazardous the carrier needs to
                    know about it. Please enter details.
                  </T>
                  <Link
                    href="https://help.freightsimple.com/freight-101/dangerous-goods"
                    external
                  >
                    Learn more
                  </Link>
                </Stack>
              }
            />
          </>
        }
        error={props.errorMessage}
      >
        <Dropdown
          options={[
            { value: "false", label: "Not dangerous" },
            { value: "true", label: "Dangerous" },
          ]}
          value={selectedValue}
          width="full"
          onChange={(v) => onOptionSelected(v === "true")}
          renderOption={(option) =>
            option.value === "true" ? (
              <DescriptionForDangerousItem_ {...props} />
            ) : (
              <>Not dangerous</>
            )
          }
          data-testid="dangerousGoodsDropdown"
        />
      </FormItem>
      <DangerousGoodsSelectUNNumberDialog
        key={`dangerous-goods-details-${detailsOpenCount}`}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        lineItem={props.lineItem}
        onDone={props.onChange}
        canadianTdgRules={props.canadianTdgRules}
      />
    </ShowFormErrors>
  );
};
export default DangerousGoodsInput;
